import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { setRecoveryEmail } from "@/lib/passwordReset";

interface Props {
  host?: { email?: string | null } | null;
  /** Called with the saved email so the dashboard can update its host state. */
  onSaved: (email: string) => void;
}

/**
 * Persistent recovery-email field on the venue-admin dashboard. Shows whether an
 * email is on file (the visible confirmation the modal lacked) and lets the user
 * add or update it with immediate feedback.
 */
const RecoveryEmailCard = ({ host, onSaved }: Props) => {
  const { toast } = useToast();
  const current = host?.email ?? "";
  const [email, setEmail] = useState(current);
  const [editing, setEditing] = useState(!current);
  const [saving, setSaving] = useState(false);

  // Re-sync when the host record loads (async) or the email changes after save.
  useEffect(() => {
    setEmail(host?.email ?? "");
    setEditing(!host?.email);
  }, [host?.email]);

  const handleSave = async () => {
    const trimmed = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast({ title: "Invalid email", description: "Enter a valid email address.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await setRecoveryEmail(trimmed);
      toast({ title: "Recovery email saved", description: "You can now reset your password by email if needed." });
      onSaved(trimmed);
      setEditing(false);
    } catch (err: any) {
      toast({ title: "Couldn't save", description: err?.message ?? "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-[10px] border border-console-line bg-console-surface p-4">
      <div className="flex items-center gap-2 mb-2.5">
        <Mail className="h-4 w-4 text-console-primary shrink-0" />
        <span className="font-geist font-semibold text-[13px] text-console-text">Recovery email</span>
        {current && !editing && (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-geist-mono text-console-ok">
            <Check className="h-3 w-3" /> On file
          </span>
        )}
      </div>

      {current && !editing ? (
        <div className="flex items-center justify-between gap-3">
          <span className="font-geist text-[13px] text-console-muted truncate">{current}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setEmail(current); setEditing(true); }}
            className="shrink-0 h-8 px-3 text-[12px] font-medium font-geist text-console-muted hover:text-console-text hover:bg-white/5 border border-console-line rounded-[6px]"
          >
            Update
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@venue.com"
              className="h-10 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim rounded-[6px] focus:border-console-primary focus-visible:ring-0"
            />
            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="h-10 px-4 text-[13px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]"
              >
                {saving ? "Saving…" : "Save"}
              </Button>
              {current && (
                <Button
                  variant="ghost"
                  onClick={() => { setEditing(false); setEmail(current); }}
                  className="h-10 px-3 text-[13px] font-geist text-console-muted hover:text-console-text border border-console-line rounded-[6px]"
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
          {!current && (
            <p className="font-geist text-[11px] text-console-muted mt-2">
              Add a recovery email so you can reset your password if you're ever locked out.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default RecoveryEmailCard;
